import axios from 'axios'
import {
    GroupChat,
    ConversationParticipant,
    GroupInvitation,
    CreateGenericGroupRequest,
    AddParticipantRequest,
    UpdateParticipantRoleRequest,
    GenerateInviteRequest,
    GroupChatDetailsResponse,
    GenerateInviteResponse,
    JoinViaInviteResponse,
} from '@/types/groupChat'

// Get API URL from environment
const API_URL = import.meta.env.VITE_API_URL

/**
 * Group Chat API Service
 * Handles all HTTP requests for group chat functionality
 * All requests use withCredentials: true for HTTP-only cookie authentication
 */
export const groupChatService = {
    /**
     * Create a group chat for a specific class
     * @param classId - UUID of the class
     * @returns Full conversation object with groupChat and conversationParticipants
     */
    createClassGroup: async (classId: string) => {
        const response = await axios.post(
            `${API_URL}/chat/groups/class/${classId}`,
            {},
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Create a generic group chat (not tied to a class)
     * @param data - Group creation data (name, participantIds, etc.)
     * @returns Full conversation object with groupChat and conversationParticipants
     */
    createGenericGroup: async (data: CreateGenericGroupRequest) => {
        const response = await axios.post(
            `${API_URL}/chat/groups`,
            data,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Get details of a specific group chat
     * @param conversationId - UUID of the conversation
     * @returns GroupChat object with related conversation, class, and supervisor data
     */
    getGroupDetails: async (conversationId: string): Promise<GroupChatDetailsResponse> => {
        const response = await axios.get(
            `${API_URL}/chat/groups/${conversationId}`,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Get the group chat associated with a class (if exists)
     * @param classId - UUID of the class
     * @returns GroupChat object or null if no group exists
     */
    getClassGroup: async (classId: string): Promise<GroupChat | null> => {
        const response = await axios.get(
            `${API_URL}/chat/groups/class/${classId}`,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Get all participants of a group chat with their roles and status
     * @param conversationId - UUID of the conversation
     * @returns Array of ConversationParticipant objects with user details
     */
    getParticipants: async (conversationId: string): Promise<ConversationParticipant[]> => {
        const response = await axios.get(
            `${API_URL}/chat/groups/${conversationId}/participants`,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Add a user to the group
     * @param conversationId - UUID of the conversation
     * @param data - Add participant data (userId)
     * @returns Success message
     */
    addParticipant: async (conversationId: string, data: AddParticipantRequest) => {
        const response = await axios.post(
            `${API_URL}/chat/groups/${conversationId}/participants`,
            data,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Remove a participant from the group
     * @param conversationId - UUID of the conversation
     * @param userId - UUID of the user to remove
     * @returns Success message
     */
    removeParticipant: async (conversationId: string, userId: string) => {
        const response = await axios.delete(
            `${API_URL}/chat/groups/${conversationId}/participants/${userId}`,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Current user leaves the group
     * @param conversationId - UUID of the conversation
     * @returns Success message
     */
    leaveGroup: async (conversationId: string) => {
        const response = await axios.post(
            `${API_URL}/chat/groups/${conversationId}/leave`,
            {},
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Kick a participant from the group (marks as KICKED status)
     * @param conversationId - UUID of the conversation
     * @param userId - UUID of the user to kick
     * @returns Success message
     */
    kickParticipant: async (conversationId: string, userId: string) => {
        const response = await axios.post(
            `${API_URL}/chat/groups/${conversationId}/kick/${userId}`,
            {},
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Change a participant's role
     * @param conversationId - UUID of the conversation
     * @param userId - UUID of the user
     * @param data - Role update data
     * @returns Success message
     */
    updateParticipantRole: async (
        conversationId: string,
        userId: string,
        data: UpdateParticipantRoleRequest
    ) => {
        const response = await axios.patch(
            `${API_URL}/chat/groups/${conversationId}/participants/${userId}/role`,
            data,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Generate an invite code for the group
     * @param conversationId - UUID of the conversation
     * @param data - Invite options (expiresAt, maxUses)
     * @returns Object containing invitation details, inviteLink, and inviteCode
     */
    generateInvite: async (
        conversationId: string,
        data: GenerateInviteRequest
    ): Promise<GenerateInviteResponse> => {
        const response = await axios.post(
            `${API_URL}/chat/groups/${conversationId}/invites`,
            data,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Get all active invite codes for a group
     * @param conversationId - UUID of the conversation
     * @returns Array of GroupInvitation objects
     */
    getActiveInvites: async (conversationId: string): Promise<GroupInvitation[]> => {
        const response = await axios.get(
            `${API_URL}/chat/groups/${conversationId}/invites`,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Join a group using an invite code
     * @param inviteCode - The invite code
     * @returns Success message with conversationId and groupChatId
     */
    joinViaInvite: async (inviteCode: string): Promise<JoinViaInviteResponse> => {
        const response = await axios.post(
            `${API_URL}/chat/join/${inviteCode}`,
            {},
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Deactivate an invite code
     * @param inviteCode - The invite code to revoke
     * @returns Success message
     */
    revokeInvite: async (inviteCode: string) => {
        const response = await axios.delete(
            `${API_URL}/chat/invites/${inviteCode}`,
            { withCredentials: true }
        )
        return response.data
    },

    /**
     * Search for users (students/teachers)
     * @param term - Search term
     * @returns Array of users
     */
    searchUsers: async (term: string) => {
        if (!term || term.trim().length < 2) {
            return []
        }
        // Use the generic user search endpoint
        // This endpoint should handle role-based filtering on the backend if needed
        // or return all matching users
        const response = await axios.get(`${API_URL}/users/search`, {
            params: { term },
            withCredentials: true,
        })
        return response.data
    },
}
